/**
 * The /enrolled date of birth: a masked MM/DD/YYYY text input, 18 or older (19 in AL) and no older than 122. Sent to
 * dot-com as YYYY-MM-DD.
 */

export const BIRTH_DATE_PLACEHOLDER = 'MM/DD/YYYY';
/** The oldest verified person */
export const MAX_AGE = 122;

const SEGMENT_LENGTHS = [2, 2, 4] as const;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UI_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export type BirthDateResult = { iso: string; error?: never } | { iso?: never; error: string };

/** 18, or 19 in AL */
export function minAgeFor(state: string | undefined): number {
  return state?.toUpperCase() === 'AL' ? 19 : 18;
}

/**
 * Formats typing (or a paste) as MM/DD/YYYY, e.g. "0102" → "01/02". A separator after one digit pads it ("1/" → "01")
 * and a YYYY-MM-DD paste or autofill is reordered. The slash is added once the next segment starts, so Backspace
 * over it works. Without `pad`, a one-digit segment stays as it is ("0/14/1985"), so deleting a digit leaves room to
 * type its replacement.
 */
export function formatBirthDate(value: string, pad = true): string {
  const iso = ISO_DATE.exec(value.trim());
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;

  const segments = ['', '', ''];
  let i = 0;
  for (const char of value) {
    if (i >= segments.length) break;
    if (/\d/.test(char)) {
      segments[i] += char;
      if (segments[i].length === SEGMENT_LENGTHS[i]) i++;
    } else if (segments[i].length === 1 && i < 2) {
      if (pad) segments[i] = `0${segments[i]}`;
      i++;
    }
  }
  return segments.filter(Boolean).join('/');
}

/** Checks an MM/DD/YYYY date of birth against `today` (local); the YYYY-MM-DD date, or the message to show. */
export function parseBirthDate(value: string, state?: string, today = new Date()): BirthDateResult {
  const text = value.trim();
  if (!text) return { error: 'Enter your date of birth.' };

  const match = UI_DATE.exec(text);
  const [month, day, year] = match ? [+match[1], +match[2], +match[3]] : [0, 0, 0];
  // Round-tripping through Date rejects a day the month doesn't have (02/30, 02/29 outside a leap year)
  const date = new Date(year, month - 1, day);
  if (!match || date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { error: 'Enter a valid date as MM/DD/YYYY.' };
  }

  const [ty, tm, td] = [today.getFullYear(), today.getMonth() + 1, today.getDate()];
  if (year > ty || (year === ty && (month > tm || (month === tm && day > td)))) {
    return { error: 'Enter a date in the past.' };
  }
  const age = ty - year - (tm < month || (tm === month && td < day) ? 1 : 0);
  const minAge = minAgeFor(state);
  if (age < minAge) return { error: `You must be ${minAge} or older to get a quote.` };
  if (age > MAX_AGE) return { error: `Enter a date within the last ${MAX_AGE} years.` };

  return { iso: `${match[3]}-${match[1]}-${match[2]}` };
}
